import type { ObjectId } from 'mongodb';

import type { BankAccountType } from './OrganizationSettings';

import type { ExternalReference } from './index';

export enum FinancialAccountStatus {
  active = 'active',
  inactive = 'inactive',
}

export enum FinancialAccountReferenceType {
  account = 'account',
  organization = 'organization',
}

export enum FinancialAccountPaymentMethod {
  card = 'card',
  bankAccount = 'bankAccount',
}

export enum FinancialAccountPaymentMethodType {
  card = 'Credit card',
  bankAccount = 'ACH',
}

export interface FinancialAccountBankRegister {
  accountNumber: string;
  routingNumber: string;
  bankAccountType: BankAccountType;
  accountHolderName?: string;
  isAccountValidated?: boolean;
}

export interface FinancialAccountBankResponse {
  last4: string;
  bankAccountType: string;
  accountHolderName: string;
}

export interface FinancialAccountBankAdminUserResponse {
  accountNumber: string;
  routingNumber: string;
  bankAccountType: string;
}

export type FinancialAccountBank = FinancialAccountBankRegister & {
  last4: string;
  accountHolderName: string;
  bankName?: string;
};

export interface FinancialAccountCardRegister {
  number: string;
  expirationMonth: string;
  expirationYear: string;
  cvc: string;
  cardHolderName: string;
  zipCode: string;
}

export interface FinancialAccountCardRegisterResponse extends Omit<FinancialAccountCardRegister, 'zipCode'> {
  zipCode?: string;
}

export interface FinancialAccountCardResponse {
  cardHolderName: string;
  last4: string;
  cardType?: string;
}

export type FinancialAccountCard = FinancialAccountCardRegisterResponse & FinancialAccountCardResponse;

export interface AccountFinancialAccountReference {
  id: ObjectId;
  type: FinancialAccountReferenceType.account;
}

export interface OrganizationFinancialAccountReference {
  id: ObjectId;
  type: FinancialAccountReferenceType.organization;
}

export type FinancialAccountReference = AccountFinancialAccountReference | OrganizationFinancialAccountReference;

interface BaseFinancialAccountDocument {
  id: ObjectId;
  card?: FinancialAccountCard;
  bankAccount?: FinancialAccountBank;
  externalReferenceIds: ExternalReference[];
  isSavedInConcord: boolean;
  nickname?: string;
}

export interface AccountFinancialAccountDocument extends BaseFinancialAccountDocument {
  references: AccountFinancialAccountReference[];
  isAutoPayMethod: boolean;
}

export interface OrganizationFinancialAccountDocument extends BaseFinancialAccountDocument {
  references: OrganizationFinancialAccountReference[];
  status: FinancialAccountStatus;
}

export type FinancialAccountDocument = AccountFinancialAccountDocument | OrganizationFinancialAccountDocument;

export const isAccountFinancialAccount = (doc: FinancialAccountDocument): doc is AccountFinancialAccountDocument =>
  doc.references.length > 0 && doc.references.every((ref) => ref.type === FinancialAccountReferenceType.account);

export const isOrganizationFinancialAccount = (
  doc: FinancialAccountDocument,
): doc is OrganizationFinancialAccountDocument =>
  doc.references.length > 0 &&
  doc.references.every((ref) => ref.type === FinancialAccountReferenceType.organization);

export interface FinancialAccountResponse {
  id: string;
  references: FinancialAccountReference[];
  card?: FinancialAccountCardResponse;
  bankAccount?: FinancialAccountBankResponse;
  isAutoPayMethod: boolean;
  externalReferenceIds: ExternalReference[];
}

export interface FinancialAccountPaymentMethodResponse {
  id: string;
  accountId?: string;
  organizationId?: string;
  card?: FinancialAccountCardResponse;
  bankAccount?: FinancialAccountBankResponse;
  nickname?: string;
}

export interface FinancialAccountPaymentMethodAdminUserResponse {
  id: string;
  accountId?: string;
  organizationId?: string;
  card?: FinancialAccountCardResponse;
  bankAccount?: FinancialAccountBankAdminUserResponse;
  nickname?: string;
}

export interface SavedPaymentMethodCardFullResponse {
  expirationMonth: string;
  expirationYear: string;
  cardHolderName: string;
  zipCode?: string;
  last4: string;
  cardType?: string;
}

export interface SavedPaymentMethodBankFullResponse {
  bankAccountType: string;
  accountHolderName?: string;
  last4: string;
  isAccountValidated?: boolean;
  /** Populated only when the requester is a LightReach support user AND fullResponse=true. */
  accountNumber?: string;
  /** Populated only when the requester is a LightReach support user AND fullResponse=true. */
  routingNumber?: string;
}

export interface SavedPaymentMethodFullResponse {
  id: string;
  isAutoPayMethod: boolean;
  card?: SavedPaymentMethodCardFullResponse;
  bankAccount?: SavedPaymentMethodBankFullResponse;
  nickname?: string;
}

export interface FinancialAccountRegister {
  card?: FinancialAccountCardRegister;
  bankAccount?: FinancialAccountBankRegister;
}

export interface FinancialAccountRegisterPayload {
  card?: FinancialAccountCardRegisterResponse;
  bankAccount?: FinancialAccountBankRegister;
}

export interface FinancialAccountUpsertParams {
  entityType: string;
  entityData: FinancialAccountCard | FinancialAccountBank;
  existingFinancialAccounts: FinancialAccountDocument[];
  references: FinancialAccountReference[];
}

export interface CreditCardProcessorProviderDict {
  [key: string]: CardProcessorType;
}

export enum CardProcessorType {
  visa = 'visa',
  mastercard = 'mastercard',
  americanExpress = 'americanexpress',
  discover = 'discover',
}
